import * as React from "react";
import { Tooltip, message } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormatData } from "utils/FormatData";
import { observer } from "mobx-react";
import { UploadFileStatus } from "antd/lib/upload/interface";
import { downloadProjectFile, download } from "utils/download";
import { pdfViewerStore } from "Stores/PdfViewerStore";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { onlineViewerStore } from "Stores/OnlineViewerStore";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import { wopiViewerStore } from "Stores/WOPIViewerStore";
import { withModal, WithModal } from "Components/General/Modal/withModal";
import { fileDetailsStore } from "Components/FileDetails/FileDetailsStore";
import { BCFViewerModal } from "Components/BcfViewer/BCFViewerModal";
import { modalProps } from "Pages/Project/QA/utils/modalProps";
import { locateFileInDocuments } from "utils/locateFileInDocuments";
import { useNavigate } from "react-router";
import { generatePath } from "react-router-dom";
import { xktViewerStore } from "Components/XKTViewer/Stores/XKTViewerStore";
import { apryseViewerStore } from "Stores/PdfViewerStore/ApryseViewerStore";

interface FileAttachProps extends WithModal {
  attach: API_FileAttachment;
  onMove?: () => void;
}

export const FileAttach = withModal(({ attach, onMove, showModal, hideAllModals }: FileAttachProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation(I18N_NS.PROJECT);
  const handleDownload = async () => {
    const hide = message.loading(t("Action in progress..."));
    try {
      if (attach.accessLevel === "DOWNLOAD") {
        await downloadProjectFile({ id: attach.id, state: "ACTIVE" }, { withQR: projectsDataStore.isQRForce() });
      } else {
        await downloadPdfVersionOfFile();
      }
    } catch (error) {
      throw error;
    } finally {
      hide();
    }
  };

  const downloadPdfVersionOfFile = async () => {
    try {
      const response = await API.getBlob(API_URL.VIEWER.getEasyViewerFile_2(projectsDataStore.projectId, attach.id));
      download(response, response.name, response.type);
    } catch (error) {
      throw error;
    }
  };

  const handleLocate = () => {
    fileDetailsStore.hideModal();
    hideAllModals();
    locateFileInDocuments(attach.parentId, attach.id);
  };

  const handleView = () => {
    if (attach.viewers.webViewer) {
      apryseViewerStore.fetch(attach.id, {
        fileName: attach.name,
        canDownload: attach.accessLevel !== "VIEW_ONLY",
      });
    } else if (attach.viewers.easyViewer) {
      pdfViewerStore.fetch(attach.id, {
        fileName: attach.name,
        canDownload: attach.accessLevel !== "VIEW_ONLY",
      });
    } else if (attach.viewers.office365) {
      wopiViewerStore.fetch(attach.id, { fileName: attach.name });
    } else if (attach.viewers.onlineViewer) {
      onlineViewerStore.fetch(attach.id, { fileName: attach.name });
    } else if (attach.viewers.bimViewer) {
      xktViewerStore.initViewer({
        fileName: attach.name,
        fileId: attach.id as number,
        parentFolderId: attach.parentId,
      });
    } else if (attach.viewers.bcfViewer) {
      showModal(BCFViewerModal, {
        modalProps: {
          ...modalProps,
          maskClosable: true,
          width: "100%",
          title: `${t("File")}: ${attach.name}`,
          style: { maxWidth: "1300px" },
          className: "full-height-modal with-drawer bcf-modal",
          zIndex: 1013,
        },
        componentProps: { fileId: attach.id },
      });
    }
  };

  const jumpToMeeting = () => {
    navigate(
      {
        pathname: generatePath("/project/:projectId/project-management/meetings/:meetingSeriesId/meeting/:meetingId", {
          projectId: projectsDataStore.projectId.toString(),
          meetingSeriesId: attach.meetingSeriesId.toString(),
          meetingId: attach.meetingId.toString(),
        }),
      },
      // NOTE: "tab" value should match the Tab key of
      // src/Pages/Project/ProjectManagement/Meetings/Pages/Meeting.tsx
      { state: { tab: "documentation" } }
    );
  };

  const fileSize = React.useMemo(() => (attach.size ? FormatData.formatBytes(attach.size, 1) : null), [attach.size]);

  const tooltip = React.useMemo(() => {
    let tooltip;
    const viewers = attach.viewers || {};
    if (attach.deleted) {
      tooltip = t("The file is identified as deleted");
    } else if (attach.malware) {
      tooltip = t("The file is identified as infected");
    } else if (
      attach.accessLevel === "VIEW_ONLY" &&
      !viewers.easyViewer &&
      !viewers.onlineViewer &&
      !viewers.office365
    ) {
      tooltip = t("No viewer available for this file");
    } else if (!attach.accessLevel) {
      tooltip = t("Access rights in Documents module were removed");
    }
    return tooltip;
  }, [attach?.viewers]);

  const viewable = React.useMemo(() => {
    let result = false;
    if (attach.viewers) {
      if (attach.viewers.webViewer) {
        result = true;
      } else if (attach.viewers.easyViewer) {
        result = true;
      } else if (attach.viewers.office365) {
        result = true;
      } else if (attach.viewers.onlineViewer) {
        result = true;
      } else if (attach.viewers.bcfViewer) {
        result = true;
      } else if (attach.viewers.bimViewer) {
        result = true;
      }
    }
    return result;
  }, [attach?.viewers]);

  const attachClass = classNames("attach", { deleted: attach.deleted });

  return (
    <div className={attachClass}>
      <div className="attach-filename">
        <FontAwesomeIcon icon={["far", "paperclip"]} size="lg" fixedWidth={true} className="attach-icon" />
        <div style={{ margin: "0 10px" }}>
          <div title={attach.name}>{attach.name}</div>
          <div className="attach-size">{fileSize}</div>
        </div>
      </div>
      <div className="attach-options">
        {tooltip && (
          <Tooltip placement="top" title={tooltip}>
            <FontAwesomeIcon icon="info-circle" size="lg" fixedWidth={true} />
          </Tooltip>
        )}
        <div>
          {attach?.fileActions?.download && (
            <Tooltip title={t("Download")}>
              <div className="attach-icon" onClick={handleDownload}>
                <FontAwesomeIcon icon={["far", "download"]} size="lg" fixedWidth={true} />
              </div>
            </Tooltip>
          )}
          {!isNaN(attach.parentId) && !attach.deleted && (
            <Tooltip title={t("Jump to directory")}>
              <div className="attach-icon" onClick={handleLocate}>
                <FontAwesomeIcon icon={["far", "folder"]} size="lg" fixedWidth={true} />
              </div>
            </Tooltip>
          )}
          {viewable && (
            <Tooltip title={t("Preview")}>
              <div className="attach-icon" onClick={handleView}>
                <FontAwesomeIcon icon={["far", "eye"]} size="lg" fixedWidth={true} />
              </div>
            </Tooltip>
          )}
          {attach.movable && (
            <Tooltip title={t("Move to project")}>
              <div className="attach-icon" onClick={onMove}>
                <FontAwesomeIcon icon={["far", "box-full"]} size="lg" fixedWidth={true} />
              </div>
            </Tooltip>
          )}
          {!!attach.meetingId && !!attach.meetingSeriesId && (
            <Tooltip title={t("Jump to Meeting")}>
              <div className="attach-icon" onClick={jumpToMeeting}>
                <FontAwesomeIcon icon={["far", "calendar-day"]} size="lg" fixedWidth={true} />
              </div>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
});

interface FileAttachEditableProps {
  onDelete: (uid: string) => void;
  name: string;
  percent: number;
  size: number;
  status: UploadFileStatus;
  uid: string;
}

export const FileAttachEditable: React.FunctionComponent<FileAttachEditableProps> = observer(
  ({ name, percent, size, status, uid, onDelete }) => {
    const fileSize = FormatData.formatBytes(size, 1);
    return (
      <div className="attach deletable">
        <div className="attach-filename">
          <FontAwesomeIcon icon={["far", "paperclip"]} size="lg" fixedWidth={true} className="attach-icon" />
          <div style={{ margin: "0 10px", lineHeight: "normal" }}>
            <div title={name}>{name}</div>
            <div className="attach-size">{fileSize}</div>
          </div>
        </div>
        {(!status || status === "done") && (
          <div className="attach-options text-right remove-icon" onClick={() => onDelete(uid)}>
            <FontAwesomeIcon icon={["fal", "times"]} size="lg" fixedWidth={true} className="attach-icon" />
          </div>
        )}
        {status === "uploading" && <div className="attach-progress" style={{ width: `${percent}%` }} />}
      </div>
    );
  }
);
